import { Sparkles, Trash2, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LINES = ["maternal", "paternal"];
const RELATIONSHIPS = ["brother", "sister", "half_brother", "half_sister", "offspring", "other"];

/* Fase C — panel de revisión del draft IA de Performance Relatives.
   "La IA propone, el organizador valida": cada entrada es editable y
   eliminable; NADA se guarda hasta confirmar (y aun así solo se fusiona en
   el formulario — el guardado real es el submit del HorseForm). */
export const RelativesReviewDialog = ({ draft, onClose, onApply }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (draft) setEntries((draft.relatives || []).map((r) => ({ ...r, url: r.url || "" })));
  }, [draft]);

  if (!draft) return null;
  const notes = draft.notes || [];

  const setField = (i, key) => (v) =>
    setEntries((es) => es.map((r, j) => (j === i ? { ...r, [key]: v } : r)));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-white rounded-2xl max-w-2xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto p-6 sm:p-8"
        data-testid="relatives-review-dialog"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 tracking-tight">
            <Sparkles className="w-5 h-5 text-[#1D4D3B]" aria-hidden="true" />
            {t("pr.reviewTitle")}
          </DialogTitle>
          <DialogDescription>{t("pr.reviewSubtitle")}</DialogDescription>
        </DialogHeader>

        {notes.length > 0 && (
          <div className="flex flex-col gap-1.5 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3" data-testid="pr-review-notes">
            {notes.map((n, i) => (
              <span key={i} className="inline-flex items-start gap-2 text-xs text-amber-800">
                <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                {n}
              </span>
            ))}
          </div>
        )}

        {entries.length === 0 ? (
          <p className="text-sm text-[#737373] py-4" data-testid="pr-review-empty">{t("pr.reviewEmpty")}</p>
        ) : (
          <div className="flex flex-col gap-3">
            <span className="text-xs text-[#737373]">{t("pr.detected", { count: entries.length })}</span>
            {entries.map((r, i) => (
              <div key={i} className="rounded-2xl border border-black/5 bg-[#F9F9F9] p-3.5 flex flex-col gap-2" data-testid={`pr-review-row-${i}`}>
                <div className="grid grid-cols-[1fr_1.2fr_36px] gap-2 items-center">
                  <Select value={r.line} onValueChange={setField(i, "line")}>
                    <SelectTrigger data-testid={`pr-review-line-${i}`} className="h-9 rounded-xl border-black/10 bg-white text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {LINES.map((l) => (
                        <SelectItem key={l} value={l}>{t(`pr.${l}Line`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={r.relationship} onValueChange={setField(i, "relationship")}>
                    <SelectTrigger data-testid={`pr-review-relationship-${i}`} className="h-9 rounded-xl border-black/10 bg-white text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {RELATIONSHIPS.map((rel) => (
                        <SelectItem key={rel} value={rel}>{t(`pr.rel_${r.line}_${rel}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    data-testid={`pr-review-remove-${i}`}
                    aria-label={t("pr.entryRemove")}
                    onClick={() => setEntries((es) => es.filter((_, j) => j !== i))}
                    className="h-8 w-8 rounded-full flex items-center justify-center text-[#0A0A0A]/30 hover:text-red-700 hover:bg-red-50 transition-colors duration-200"
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
                <Input
                  data-testid={`pr-review-name-${i}`}
                  value={r.name}
                  placeholder={t("pr.namePh")}
                  onChange={(e) => setField(i, "name")(e.target.value)}
                  className={`h-9 rounded-xl bg-white ${r.name ? "border-black/10" : "border-amber-200 bg-amber-50/40"}`}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid={`pr-review-level-${i}`} value={r.level || ""} placeholder={t("pr.levelPh")}
                         onChange={(e) => setField(i, "level")(e.target.value)} className="h-9 rounded-xl border-black/10 bg-white" />
                  <Input data-testid={`pr-review-result-${i}`} value={r.result || ""} placeholder={t("pr.resultPh")}
                         onChange={(e) => setField(i, "result")(e.target.value)} className="h-9 rounded-xl border-black/10 bg-white" />
                </div>
                {(r.sire || r.dam || r.discipline) && (
                  <span className="text-[11px] text-[#737373]">
                    {[r.discipline && t(`disc.${r.discipline}`), r.sire, r.dam].filter(Boolean).join(" · ")}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="pr-review-discard" className="rounded-full h-11 px-6">
            {t("pr.discard")}
          </Button>
          <Button
            type="button"
            data-testid="pr-review-confirm"
            disabled={entries.filter((r) => r.name.trim()).length === 0}
            onClick={() => onApply(entries.filter((r) => r.name.trim()))}
            className="rounded-full h-11 px-7 bg-[#0A0A0A] text-white hover:bg-black/80 transition-colors duration-300"
          >
            {t("pr.confirmAll", { count: entries.filter((r) => r.name.trim()).length })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
